import * as React from 'react';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose } from 'redux';

import type WorkOrderModalViewModel from 'ab-viewModels/scheduleBoardWorkOrderModal.viewModel';

import * as TimeOption from '@acceligentllc/shared/utils/timeOption';

import TimeZoneSourceType from 'ab-enums/timezoneSourceType.enum';

import LockedValue from 'af-components/LockedValue';
import TimezoneSourceBanner from 'af-components/TimezoneSourceBanner';

import * as TimezoneActions from 'af-actions/timezone';

import { renderWorkOrderInfoData } from '../..';
import styles from './styles.module.scss';
import Changes from '../../Changes';

interface OwnProps {
	workOrderDataToShow: WorkOrderModalViewModel;
	currentWorkOrderData: WorkOrderModalViewModel;
	companyTimezone: Nullable<string>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const ShiftSection: React.FC<Props> = (props) => {
	const { workOrderDataToShow, currentWorkOrderData, companyTimezone, findTimezone } = props;

	const [timezone, setTimezone] = React.useState<Nullable<string>>(null);
	const [timezoneSourceType, setTimezoneSourceType] = React.useState<Nullable<TimeZoneSourceType>>(null);

	React.useEffect(() => {
		const getTimezone = async () => {
			let _timezone: Nullable<string> = null;
			let _timezoneSourceType: Nullable<TimeZoneSourceType> = null;

			const latitude = workOrderDataToShow.addresses?.[0]?.latitude;
			const longitude = workOrderDataToShow.addresses?.[0]?.longitude;

			if ((latitude === 0 || latitude) && (longitude === 0 || longitude)) {
				const result = await findTimezone(latitude, longitude);
				_timezone = result.timezone;
				if (_timezone) {
					_timezoneSourceType = TimeZoneSourceType.WORK_ORDER;
					setTimezone(_timezone);
					setTimezoneSourceType(_timezoneSourceType);
					return;
				}
			}

			_timezone = workOrderDataToShow.jobTimezone ?? companyTimezone ?? null;
			_timezoneSourceType = !!workOrderDataToShow.jobTimezone ? TimeZoneSourceType.JOB : TimeZoneSourceType.COMPANY;

			setTimezone(_timezone);
			setTimezoneSourceType(_timezoneSourceType);
		};
		getTimezone();
	}, [companyTimezone, findTimezone, workOrderDataToShow.addresses, workOrderDataToShow.jobTimezone]);

	const sectionHasChanges = React.useMemo(() => {

		return workOrderDataToShow?.shiftName?.toUpperCase() !== currentWorkOrderData?.shiftName?.toUpperCase()
			|| workOrderDataToShow?.timeToStart !== currentWorkOrderData?.timeToStart
			|| workOrderDataToShow?.timeToEnd !== currentWorkOrderData?.timeToEnd;
	}, [
		currentWorkOrderData?.shiftName,
		currentWorkOrderData?.timeToEnd,
		currentWorkOrderData?.timeToStart,
		workOrderDataToShow?.shiftName,
		workOrderDataToShow?.timeToEnd,
		workOrderDataToShow?.timeToStart,
	]);

	return (
		<div className={styles.container}>
			<TimezoneSourceBanner
				timezoneInUse={timezone}
				timezoneSourceType={timezoneSourceType}
			/>
			{sectionHasChanges &&
				<Changes modifiedData={sectionHasChanges} />
			}
			<div className={styles.shift}>
				<div>
					<LockedValue
						label="Shift"
						value={renderWorkOrderInfoData(workOrderDataToShow?.shiftName?.toUpperCase(), currentWorkOrderData?.shiftName?.toUpperCase())}
					/>
				</div>
				<div>
					<LockedValue
						label="Work Time Start"
						value={renderWorkOrderInfoData(
							TimeOption.format(workOrderDataToShow?.timeToStart),
							TimeOption.format(currentWorkOrderData?.timeToStart)
						)}
					/>
				</div>
				<div>
					<LockedValue
						label="Work Time End"
						value={renderWorkOrderInfoData(
							TimeOption.format(workOrderDataToShow?.timeToEnd),
							TimeOption.format(currentWorkOrderData?.timeToEnd)
						)}
					/>
				</div>
			</div>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		findTimezone: TimezoneActions.find,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	connector,
	React.memo
);

export default enhance(ShiftSection);
